// ============================================================================
// LocationPicker — pick a session location three ways, fastest first:
//   1. SAVED PLACES — one-tap chips from the trainer's saved locations
//      (sorted by use_count), wires location_id.
//   2. ADDRESS SEARCH — Nominatim via /api/locations/search; picking a hit can
//      optionally be saved as a place for next time.
//   3. FREE TEXT — just type it (location_text), no geocoding needed.
// The committed value is a {location_id, location_text} pair on the session.
// Optional-first: leaving it blank is fine. Reduced-motion aware.
// ============================================================================
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "../../components";
import { IconMapPin } from "../../components/icons";
import {
  useCreateLocation,
  useLocationSearch,
  useLocations,
} from "../../hooks/locations";
import type { LocationSearchHit } from "../../api";
import { useDebounced } from "./useDebounced";
import { cn } from "../../lib/cn";

export interface LocationValue {
  location_id: number | null;
  location_text: string | null;
}

interface LocationPickerProps {
  value: LocationValue;
  onChange: (v: LocationValue) => void;
}

export function LocationPicker({ value, onChange }: LocationPickerProps) {
  const { t } = useTranslation("sessions");
  const savedQuery = useLocations();
  const createLocation = useCreateLocation();

  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 350);
  const searchQuery = useLocationSearch(debounced);

  const saved = useMemo(() => {
    const list = savedQuery.data ?? [];
    return [...list].sort((a, b) => b.use_count - a.use_count).slice(0, 8);
  }, [savedQuery.data]);

  const pickSaved = (id: number, label: string) => {
    onChange({ location_id: id, location_text: label });
    setSearch("");
  };

  const pickHit = (hit: LocationSearchHit) => {
    const label = (hit.formatted_address || hit.name || "").trim();
    onChange({ location_id: null, location_text: label });
    setSearch("");
    // Best-effort save so it's a quick-pick next time. Non-blocking; failure is
    // silent (the free-text value is already committed above).
    if (label && hit.lat != null && hit.lng != null) {
      createLocation.mutate({
        name: hit.name ?? label,
        formatted_address: hit.formatted_address ?? label,
        lat: hit.lat,
        lng: hit.lng,
      });
    }
  };

  const clear = () => {
    onChange({ location_id: null, location_text: null });
    setSearch("");
  };

  const hits = searchQuery.data ?? [];
  const showHits = debounced.trim().length >= 3;

  return (
    <div className="space-y-3">
      {/* Saved places — one-tap chips */}
      {saved.length > 0 && (
        <div>
          <p className="mb-1.5 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
            {t("location.saved")}
          </p>
          <div className="flex flex-wrap gap-2">
            {saved.map((loc) => {
              const label = (loc.name || loc.formatted_address || "").trim();
              const active = value.location_id === loc.id;
              return (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => pickSaved(loc.id, label)}
                  aria-pressed={active}
                  className={cn(
                    "inline-flex max-w-full items-center gap-1.5 rounded-pill px-3 py-2 text-sm font-bold transition-all duration-fast",
                    active
                      ? "bg-grad-coral text-white shadow-volt"
                      : "bg-surface-sunken text-body-muted hover:bg-line ring-1 ring-inset ring-line",
                  )}
                >
                  <IconMapPin width={15} height={15} className="shrink-0" />
                  <span className="truncate">{label || `#${loc.id}`}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Address search (Nominatim) */}
      <div>
        <TextField
          label={t("location.search")}
          hideLabel
          placeholder={t("location.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputMode="search"
          iconLeft={<IconMapPin width={18} height={18} />}
        />
        {showHits && (
          <div className="mt-2 overflow-hidden rounded-md ring-1 ring-line">
            {searchQuery.isLoading ? (
              <p className="px-4 py-3 text-sm text-body-soft">
                {t("location.searching")}
              </p>
            ) : hits.length === 0 ? (
              <p className="px-4 py-3 text-sm text-body-soft">
                {t("location.noResults")}
              </p>
            ) : (
              <ul className="max-h-56 divide-y divide-line overflow-y-auto">
                {hits.slice(0, 8).map((hit, i) => {
                  const label = (hit.formatted_address || hit.name || "").trim();
                  return (
                    <li key={`${label}-${i}`}>
                      <button
                        type="button"
                        onClick={() => pickHit(hit)}
                        className="flex w-full items-start gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-surface-sunken focus-visible:outline-none focus-visible:bg-surface-sunken"
                      >
                        <IconMapPin
                          width={16}
                          height={16}
                          className="mt-0.5 shrink-0 text-coral"
                        />
                        <span className="text-sm text-body">{label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Free text */}
      <TextField
        label={t("location.free")}
        placeholder={t("location.freePlaceholder")}
        value={value.location_text ?? ""}
        onChange={(e) =>
          onChange({ location_id: null, location_text: e.target.value || null })
        }
        suffix={
          value.location_text || value.location_id != null ? (
            <button
              type="button"
              onClick={clear}
              className="pointer-events-auto font-bold text-coral hover:underline"
            >
              {t("location.clear")}
            </button>
          ) : undefined
        }
      />
    </div>
  );
}
